import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Checkbox,
  Collapse,
  Snackbar,
  Alert,
  Autocomplete,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Business as BusinessIcon,
  CalendarToday,
  Receipt,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import {
  freightPayCreate,
  freightPayList,
  paymentPrint,
  paySettlement,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import EditFreightPayment from "./EditFreightPayment";
import { T } from "../../../theme";
import { brandColor as brand } from "../../../theme";
import { pdf } from "@react-pdf/renderer";
import { PaymentInvoiceDocument } from "./PaymentInvoicePDF";
import { useDebounce } from "../../../Hooks/useDebounce";

// ─── Shared Styles (same as FreightInvoice) ───────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const successBtn = {
  ...sq,
  border: `1.5px solid ${T.successBorder}`,
  bgcolor: T.successBg,
  color: T.success,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: T.success, boxShadow: "none" },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const disabledFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: "#f3f4f6",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&.Mui-disabled fieldset": { border: `1.5px solid ${T.border}` },
  },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.white },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ─── Helpers ─────────────────────────────────────────────────────
const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(amount || 0);

// ─── ActionButton (same as FreightInvoice) ────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete,
  isEdit,
  isPrint,
  isSuccess,
  isView,
  onClick,
  disabled,
}) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isPrint
        ? T.success
        : isSuccess
          ? T.success
          : isView
            ? "#8b5cf6"
            : T.primary;
  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isPrint
        ? T.successBg
        : isSuccess
          ? T.successBg
          : isView
            ? "#f3f0ff"
            : T.primaryBg;
  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isPrint
        ? T.successBorder
        : isSuccess
          ? T.successBorder
          : isView
            ? "#c4b5fd"
            : T.border;
  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isPrint
        ? "#d1fae5"
        : isSuccess
          ? "#d1fae5"
          : isView
            ? "#ede9fe"
            : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            width: 20,
            height: 20,
            ...sq,
            color: c,
            bgcolor: bg,
            border: `1px solid ${bd}`,
            transition: "all 0.18s",
            "&:hover": {
              bgcolor: hBg,
              borderColor: c,
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              opacity: 0.4,
              transform: "none",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 12 } })}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ─── TruncatedCell ───────────────────────────────────────────────
const TruncatedCell = ({ text, maxWidth = 140 }) => (
  <Tooltip title={text || "—"} arrow>
    <Typography
      sx={{
        fontSize: "0.72rem",
        color: T.text,
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontWeight: 500,
      }}
    >
      {text || "—"}
    </Typography>
  </Tooltip>
);

// ─── StatCard (same style as FreightInvoice summary) ─────────────
const StatCard = ({ title, value, color, icon, borderColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      ...sq,
      border: `1.5px solid ${borderColor || T.border}`,
      bgcolor: T.white,
      boxShadow: "0 1px 6px rgba(79,70,229,0.06)",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      transition: "all 0.2s",
      "&:hover": {
        borderColor: color,
        boxShadow: `0 3px 12px ${color}22`,
        transform: "translateY(-1px)",
      },
    }}
  >
    {/* Icon Box */}
    <Box
      sx={{
        width: 38,
        height: 38,
        ...sq,
        bgcolor: `${color}14`,
        border: `1.5px solid ${color}30`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>

    {/* Text */}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: "0.58rem",
          color: T.textMuted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          lineHeight: 1,
          mb: 0.3,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 800,
          color,
          lineHeight: 1.1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Paper>
);

// ─── LRNumbersCell ───────────────────────────────────────────────
const LRNumbersCell = ({ freights }) => {
  if (!freights || freights.length === 0)
    return <Typography sx={{ fontSize: "0.72rem" }}>—</Typography>;

  const tooltipContent = (
    <Box sx={{ p: 0.45, minWidth: 150 }}>
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "70px 90px",
          columnGap: 0.5,
          mb: 0.35,
          pb: 0.25,
          borderBottom: "1px solid rgba(255,255,255,0.16)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.56rem",
            fontWeight: 800,
            color: "rgba(255,255,255,0.55)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            lineHeight: 1.1,
          }}
        >
          LR No.
        </Typography>
        <Typography
          sx={{
            fontSize: "0.56rem",
            fontWeight: 800,
            color: "rgba(255,255,255,0.55)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            lineHeight: 1.1,
          }}
        >
          Truck No.
        </Typography>
      </Box>

      {/* Rows */}
      {freights.map((f, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "70px 90px",
            columnGap: 0.5,
            py: 0.2,
            borderBottom:
              i < freights.length - 1
                ? "1px solid rgba(255,255,255,0.06)"
                : "none",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.15,
            }}
          >
            {f.lr_number || `#${f.freight_id}`}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.62rem",
              color: "rgba(255,255,255,0.78)",
              fontWeight: 500,
              lineHeight: 1.15,
            }}
          >
            {f.truck_no || "—"}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const displayText =
    freights.length === 1
      ? freights[0].lr_number || `#${freights[0].freight_id}`
      : `${freights[0].lr_number || `#${freights[0].freight_id}`} ..`;

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#1e1b4b",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 4px 14px rgba(0,0,0,0.28)",
            borderRadius: "5px",
            maxWidth: 220,
            p: 0,
          },
        },
        arrow: {
          sx: { color: "#1e1b4b" },
        },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 600,
            color: T.text,
            cursor: freights.length > 1 ? "help" : "default",
            lineHeight: 1.1,
          }}
        >
          {displayText}
        </Typography>

        {freights.length > 1 && (
          <Chip
            label={`+${freights.length - 1}`}
            size="small"
            sx={{
              ...sq,
              height: 13,
              fontSize: "0.46rem",
              fontWeight: 700,
              bgcolor: T.primaryBg,
              color: T.primary,
              border: `1px solid ${T.border}`,
              "& .MuiChip-label": { px: 0.35 },
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

// ─── MobilePaymentCard ───────────────────────────────────────────
const MobilePaymentCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onView,
  onAddPayment,
  onEdit,
  onPrint,
  onSettle,
}) => {
  const isSettled = row.is_settlement === true;
  const isNegative = row.rem_amount < 0;

  const firstFreight = row.freights?.[0] || {};
  

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      {/* Top Row */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <PaymentIcon
            sx={{ fontSize: 18, color: expanded ? T.white : T.primary }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 0.2 }}
          >
            <BusinessIcon sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              sx={{ fontSize: "0.65rem", color: T.textSub, fontWeight: 500 }}
            >
              {firstFreight.party_name || "—"}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 0.3 }}
          >
            <CalendarToday sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography sx={{ fontSize: "0.62rem", color: T.textMuted }}>
              {formatDate(row.created_at)}
            </Typography>
          </Box>
          {/* Status Chip */}
          {isSettled ? (
            <Chip
              label="Settled"
              size="small"
              icon={<CheckCircle sx={{ fontSize: 10 }} />}
              sx={{
                ...sq,
                bgcolor: T.successBg,
                color: T.success,
                border: `1px solid ${T.successBorder}`,
                fontSize: "0.55rem",
                height: 16,
                width: "fit-content",
                "& .MuiChip-icon": { color: T.success, ml: 0.5 },
              }}
            />
          ) : isNegative ? (
            <Chip
              label="Overpaid"
              size="small"
              sx={{
                ...sq,
                bgcolor: T.redBg,
                color: T.red,
                border: `1px solid ${T.redBorder}`,
                fontSize: "0.55rem",
                height: 16,
                width: "fit-content",
              }}
            />
          ) : (
            <Chip
              label="Pending"
              size="small"
              sx={{
                ...sq,
                bgcolor: "#fffbeb",
                color: "#d97706",
                border: "1px solid #fde68a",
                fontSize: "0.55rem",
                height: 16,
                width: "fit-content",
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Remaining
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.8rem",
                color: isNegative ? T.red : isSettled ? T.success : "#d97706",
              }}
            >
              {formatCurrency(row.rem_amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {/* Expanded */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            {/* Amount Summary */}
            <Box
              sx={{
                p: 1,
                bgcolor: T.primaryLight,
                border: `1.5px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                }}
              >
                💰 Amount Summary
              </Typography>
              <Grid container spacing={0.5}>
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>
                    {formatCurrency(row.total_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Paid
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: T.success,
                    }}
                  >
                    {formatCurrency(row.paid_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                    }}
                  >
                    Remaining
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: isNegative
                        ? T.red
                        : isSettled
                          ? T.success
                          : "#d97706",
                    }}
                  >
                    {formatCurrency(row.rem_amount)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* LR IDs */}
            {row.freights?.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.5,
                  }}
                >
                  🚛 LR IDs
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {row.freights.map((f, i) => (
                    <Chip
                      key={i}
                      label={`#${f.freight_id}`}
                      size="small"
                      sx={{
                        ...sq,
                        bgcolor: T.primaryBg,
                        color: T.primary,
                        border: `1px solid ${T.border}`,
                        fontSize: "0.55rem",
                        height: 18,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Payment Transactions */}
            {row.payments?.length > 0 && (
              <Box
                sx={{
                  p: 1,
                  bgcolor: T.successBg,
                  border: `1px solid ${T.successBorder}`,
                  ...sq,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: T.success,
                    mb: 0.5,
                  }}
                >
                  💳 Payment Transactions ({row.payments.length})
                </Typography>
                <Stack spacing={0.5}>
                  {row.payments.map((p, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 0.75,
                        bgcolor: T.white,
                        ...sq,
                        border: `1px solid ${T.successBorder}`,
                      }}
                    >
                      <Grid container spacing={0.5}>
                        <Grid item xs={6}>
                          <Typography
                            sx={{ fontSize: "0.58rem", color: T.textMuted }}
                          >
                            Amount
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              color: T.success,
                            }}
                          >
                            ₹{p.pay_amount}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{ fontSize: "0.58rem", color: T.textMuted }}
                          >
                            Mode
                          </Typography>
                          <Typography
                            sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                          >
                            {p.pay_mode || "—"}
                          </Typography>
                        </Grid>
                        {p.payment_to && (
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: "0.58rem", color: T.textMuted }}
                            >
                              To
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                            >
                              {p.payment_to}
                            </Typography>
                          </Grid>
                        )}
                        {p.upi_check_no && (
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: "0.58rem", color: T.textMuted }}
                            >
                              Ref
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.65rem", fontWeight: 500 }}
                            >
                              {p.upi_check_no}
                            </Typography>
                          </Grid>
                        )}
                        {p.remark && (
                          <Grid item xs={12}>
                            <Typography
                              sx={{ fontSize: "0.6rem", color: T.textMuted }}
                            >
                              Remark: {p.remark}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            {!isSettled && (
              <>
                <Button
                  size="small"
                  startIcon={<CheckCircle sx={{ fontSize: 13 }} />}
                  onClick={(e) => onSettle(e, row.freight_payment_id)}
                  sx={{ ...successBtn, height: 30, px: 1.5 }}
                >
                  Settle
                </Button>
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                  onClick={() => onAddPayment(row)}
                  sx={{ ...primaryBtn, height: 30, px: 1.5 }}
                >
                  Add Pay
                </Button>
              </>
            )}
            <Button
              size="small"
              startIcon={<ViewIcon sx={{ fontSize: 13 }} />}
              onClick={() => onView(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              View
            </Button>
            <Button
              size="small"
              startIcon={<PrintIcon sx={{ fontSize: 13 }} />}
              onClick={() => onPrint(row)}
              sx={{ ...successBtn, height: 30, px: 1.5 }}
            >
              Print
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────
export default function FreightPaymentTable() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFilters, setShowFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [freightPaymentList, setFreightPayList] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [settleAnchorEl, setSettleAnchorEl] = useState(null);
  const [settleId, setSettleId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [lrNumberFilter, setLrNumberFilter] = useState("");
  const [payDateFilter, setPayDateFilter] = useState("");

  // ── Debounced values (sent to API after 500ms) ──
  const debouncedLrNumber = useDebounce(lrNumberFilter, 500);
  const debouncedPayDate = useDebounce(payDateFilter, 500);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const handleCardToggle = (id) =>
    setExpandedCardId((prev) => (prev === id ? null : id));

  const [rows, setRows] = useState([
    { pay_amount: "", payment_to: "", pay_mode: "", upi_check_no: "" },
  ]);
  const [formData, setFormData] = useState({
    total_amount: "",
    paid_amount: "",
    bal_amount: "",
    remark: "",
  });

  const fetchFreightPayment = useCallback(
    async (pageNo = 1, lrNumber = "", payDate = "") => {
      setLoading(true);
      try {
        const data = await freightPayList(pageNo, {
          lr_number: lrNumber,
          pay_date: payDate,
        });

        const list = data?.results || [];
        if (pageNo === 1) setFreightPayList(list);
        else setFreightPayList((prev) => [...prev, ...list]);
        setHasMore(!!data?.next);
        setPage(pageNo);
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    setFreightPayList([]);
    setPage(1);
    setExpandedCardId(null);
    fetchFreightPayment(1, debouncedLrNumber, debouncedPayDate);
  }, [debouncedLrNumber, debouncedPayDate]);

  const fetchMoreData = async () => {
    if (!hasMore || loading) return;
    await fetchFreightPayment(page + 1, debouncedLrNumber, debouncedPayDate);
  };

  const handleSettle = async (id) => {
    await paySettlement(id);
    fetchFreightPayment(1, debouncedLrNumber, debouncedPayDate);
    setSnackbar({
      open: true,
      message: "Payment settled successfully",
      severity: "success",
    });
    setSettleAnchorEl(null);
    setSettleId(null);
  };

    // ── Updated handleAddPayment (With Smart Prefill Logic) ──
  const handleAddPayment = (row) => {
    setSelectedRow(row);
    setFormData({
      total_amount: row.total_amount,
      paid_amount: row.paid_amount,
      bal_amount: row.rem_amount,
      remark: "",
    });

    let autoName = "";
    if (row.payments && row.payments.length > 0) {
      const lastPayment = [...row.payments].reverse().find(p => p.payment_to && p.payment_to.trim() !== "");
      if (lastPayment) {
        autoName = lastPayment.payment_to.trim();
      }
    }
    
    if (!autoName && row.freights && row.freights.length > 0) {
      const freightWithPaymentTo = row.freights.find(f => f.payment_to && f.payment_to.trim() !== "");
      if (freightWithPaymentTo) {
        autoName = freightWithPaymentTo.payment_to.trim();
      }
    }

    setRows([
      { pay_amount: "", payment_to: autoName, pay_mode: "", upi_check_no: "" },
    ]);
    
    setOpenAddDialog(true);
  };

  const handleAddRow = () =>
    setRows([
      ...rows,
      { pay_amount: "", payment_to: "", pay_mode: "", upi_check_no: "" },
    ]);

  const handleDeleteRow = (index) =>
    setRows(rows.filter((_, i) => i !== index));

  const handleRowChange = (index, e) => {
    const { name, value } = e.target;
    const newRows = [...rows];
    newRows[index][name] = value;
    setRows(newRows);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    const validRows = rows.filter(
      (row) => row.pay_amount && Number(row.pay_amount) > 0,
    );
    if (validRows.length === 0) {
      setSnackbar({
        open: true,
        message: "Please fill at least one pay amount",
        severity: "error",
      });
      return;
    }
    const existingPayments = selectedRow?.payments ?? [];
    const totalPayAmount = validRows.reduce(
      (acc, row) => acc + Number(row.pay_amount),
      0,
    );
    const addPayment = {
      ...selectedRow,
      payments: [
        ...existingPayments,
        ...validRows.map((row) => ({
          pay_amount: row.pay_amount,
          payment_to: row.payment_to,
          upi_check_no: row.upi_check_no,
          pay_mode: row.pay_mode,
          remark: formData.remark,
        })),
      ],
      rem_amount: (Number(selectedRow.rem_amount) - totalPayAmount).toFixed(2),
      paid_amount: (Number(selectedRow.paid_amount) + totalPayAmount).toFixed(
        2,
      ),
    };
    await freightPayCreate(addPayment.freight_payment_id, addPayment);
    setSnackbar({
      open: true,
      message: "Payment added successfully",
      severity: "success",
    });
    setOpenAddDialog(false);
    fetchFreightPayment(1, debouncedLrNumber, debouncedPayDate);
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setOpenViewDialog(true);
  };
  const handleEdit = (row) => {
    setSelectedRow(row);
    setOpenEditDialog(true);
  };

  const handlePrintSingleRow = async (row) => {
    const blob = await pdf(<PaymentInvoiceDocument data={row} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");
    const exportData = freightPaymentList.map((row, i) => ({
      "S.No": i + 1,
      "Payment ID": row.freight_payment_id,
      Branch: row.branch_name,
      "LR IDs":
        row.freights?.map((f) => f.freight_id).join(", ") || row.freight_id,
      "Total Amount": row.total_amount,
      "Paid Amount": row.paid_amount,
      Remaining: row.rem_amount,
      "Payment Date": formatDate(row.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LR Payments");
    XLSX.writeFile(
      wb,
      `Freight_Payments_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const clearFilters = () => {
    setLrNumberFilter("");
    setPayDateFilter("");
  };

  const totalPayments = freightPaymentList.length;
  const totalAmount = freightPaymentList.reduce(
    (sum, r) => sum + (r.total_amount || 0),
    0,
  );
  const totalPaid = freightPaymentList.reduce(
    (sum, r) => sum + (r.paid_amount || 0),
    0,
  );

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -4, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stats Cards ── */}
      <Grid container spacing={0.75} sx={{ mt: 1.5, mb: 1.5 }}>
        <Grid item xs={4} sm={4} md={4}>
          <StatCard
            title="Total Payments"
            value={totalPayments}
            color={T.primary}
            borderColor={T.border}
            icon={<Receipt sx={{ fontSize: 18, color: T.primary }} />}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <StatCard
            title="Total Amount"
            value={formatCurrency(totalAmount)}
            color={T.success}
            borderColor={T.successBorder}
            icon={<PaymentIcon sx={{ fontSize: 18, color: T.success }} />}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={4}>
          <StatCard
            title="Total Paid"
            value={formatCurrency(totalPaid)}
            color={T.red}
            borderColor={T.redBorder}
            icon={<CheckCircle sx={{ fontSize: 18, color: T.red }} />}
          />
        </Grid>
      </Grid>

      {/* ── Toolbar ── */}
      <Box sx={{ mt: 2, mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              size="small"
              placeholder="Search by LR No…"
              fullWidth
              value={lrNumberFilter}
              onChange={(e) => setLrNumberFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Date Filter */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              size="small"
              type="date"
              label="Payment Date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={payDateFilter}
              onChange={(e) => setPayDateFilter(e.target.value)}
              sx={filterInputSx}
            />
          </Grid>

          {/* Action Icons */}
          <Grid item xs={6} sm="auto" md="auto">
            <Stack direction="row" spacing={0.75} sx={{ height: 34 }}>
              <Tooltip title="Clear Filters" arrow>
                <Button
                  sx={{ ...dangerBtn, minWidth: 34, px: 0 }}
                  onClick={clearFilters}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Refresh" arrow>
                <Button
                  sx={{ ...iconBtn, minWidth: 34, px: 0 }}
                  onClick={() =>
                    fetchFreightPayment(1, debouncedLrNumber, debouncedPayDate)
                  }
                >
                  <RefreshIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
              <Tooltip title="Export Excel" arrow>
                <Button
                  sx={{ ...successBtn, minWidth: 34, px: 0 }}
                  onClick={handleExportExcel}
                >
                  <DownloadIcon sx={{ fontSize: 15 }} />
                </Button>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Count */}
          <Grid item xs={12} sm="auto" md="auto">
            <Box
              sx={{
                height: 34,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.white,
                color: T.primary,
                border: `1.5px solid ${T.border}`,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            >
              <PaymentIcon sx={{ fontSize: 15, color: T.primary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                  lineHeight: 1,
                }}
              >
                {totalPayments} Records
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* ── Table / Mobile ── */}
      {isMobile ? (
        <Box>
          {loading && freightPaymentList.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : freightPaymentList.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: `1.5px dashed ${T.border}`,
                bgcolor: T.white,
                ...sq,
              }}
            >
              <PaymentIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: T.text,
                  mb: 0.5,
                  fontSize: "0.9rem",
                }}
              >
                No payment records found
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: T.textSub }}>
                Try adjusting your filters
              </Typography>
            </Box>
          ) : (
            <Box
              id="payment-scroll-mobile"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              <InfiniteScroll
                dataLength={freightPaymentList.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <CircularProgress size={20} sx={{ color: T.primary }} />
                  </Box>
                }
                scrollableTarget="payment-scroll-mobile"
              >
                {freightPaymentList.map((row, idx) => (
                  <MobilePaymentCard
                    key={row.freight_payment_id}
                    row={row}
                    idx={idx}
                    expanded={expandedCardId === row.freight_payment_id}
                    onToggle={() => handleCardToggle(row.freight_payment_id)}
                    onView={handleView}
                    onAddPayment={handleAddPayment}
                    onEdit={handleEdit}
                    onPrint={handlePrintSingleRow}
                    onSettle={(e, id) => {
                      setSettleAnchorEl(e.currentTarget);
                      setSettleId(id);
                    }}
                  />
                ))}
              </InfiniteScroll>
            </Box>
          )}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            height: "calc(100vh - 230px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="payment-scroll-container"
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={freightPaymentList.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="payment-scroll-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 900,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                    height: 28,
                  },
                  "& td": { height: 26 },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* Settle - sticky left */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        top: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 50,
                      }}
                    >
                      Settle
                    </TableCell>

                    {/* S.No - sticky left */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 50,
                        top: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 40,
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>LR No</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>Party</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Total Amt</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Paid Amt</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Remaining</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 90 }}>Pay Date</TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        top: 0,
                        zIndex: 30,
                        bgcolor: `${T.headerBg} !important`,
                        minWidth: 110,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && freightPaymentList.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : freightPaymentList.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <PaymentIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No payment records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    freightPaymentList.map((row, idx) => {
                      const isSettled = row.is_settlement === true;
                      const isNegative = row.rem_amount < 0;
                      const isEven = idx % 2 === 0;
                      const rowBg = isEven ? T.baseRow : T.altRow;
                      const stickyCell = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      };

                      const firstFreight = row.freights?.[0] || {};

                      return (
                        <TableRow
                          key={row.freight_payment_id}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* Settle - sticky left */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              ...stickyCell,
                              p: "2px 4px",
                            }}
                          >
                            <Checkbox
                              size="small"
                              disabled={isSettled}
                              onClick={(e) => {
                                setSettleAnchorEl(e.currentTarget);
                                setSettleId(row.freight_payment_id);
                              }}
                              sx={{
                                p: 0,
                                color: T.primary,
                                "&.Mui-checked": { color: T.primary },
                              }}
                            />
                          </TableCell>

                          {/* S.No - sticky left */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 50,
                              ...stickyCell,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <LRNumbersCell freights={row.freights} />
                          </TableCell>
                          <TableCell>
                            <TruncatedCell
                              text={firstFreight.party_name}
                              maxWidth={120}
                            />
                          </TableCell>

                          {/* Total Amt */}
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatCurrency(row.total_amount)}
                          </TableCell>

                          {/* Paid Amt */}
                          <TableCell sx={{ color: T.success, fontWeight: 600 }}>
                            {formatCurrency(row.paid_amount)}
                          </TableCell>

                          {/* Remaining */}
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              color: isNegative
                                ? T.red
                                : isSettled
                                  ? T.success
                                  : "#d97706",
                            }}
                          >
                            {formatCurrency(row.rem_amount)}
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            {isSettled ? (
                              <Chip
                                label="Settled"
                                size="small"
                                sx={{
                                  ...sq,
                                  bgcolor: T.successBg,
                                  color: T.success,
                                  border: `1px solid ${T.successBorder}`,
                                  fontSize: "0.6rem",
                                  height: 18,
                                  fontWeight: 700,
                                }}
                              />
                            ) : isNegative ? (
                              <Chip
                                label="Overpaid"
                                size="small"
                                sx={{
                                  ...sq,
                                  bgcolor: T.redBg,
                                  color: T.red,
                                  border: `1px solid ${T.redBorder}`,
                                  fontSize: "0.6rem",
                                  height: 18,
                                  fontWeight: 700,
                                }}
                              />
                            ) : (
                              <Chip
                                label="Pending"
                                size="small"
                                sx={{
                                  ...sq,
                                  bgcolor: "#fffbeb",
                                  color: "#d97706",
                                  border: "1px solid #fde68a",
                                  fontSize: "0.6rem",
                                  height: 18,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </TableCell>

                          {/* Pay Date */}
                          <TableCell>{formatDate(row.created_at)}</TableCell>

                          {/* Actions - sticky right */}
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              ...stickyCell,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="View"
                                icon={<ViewIcon />}
                                isView
                                onClick={() => handleView(row)}
                              />
                              <ActionButton
                                tip="Add Payment"
                                icon={<AddIcon />}
                                isSuccess
                                onClick={() => handleAddPayment(row)}
                                disabled={isSettled}
                              />
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEdit(row)}
                              />
                              <ActionButton
                                tip="Print"
                                icon={<PrintIcon />}
                                isPrint
                                onClick={() => handlePrintSingleRow(row)}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Add Payment Dialog ── */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            background: "#6862e0",
            px: 2.5,
            py: 1.75,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.white,
              fontSize: "0.88rem",
              letterSpacing: "0.01em",
            }}
          >
            Add Payment
          </Typography>
          <IconButton
            onClick={() => setOpenAddDialog(false)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.5,
              ...sq,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
                color: T.white,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmitPayment}>
          <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Total Amount"
                  type="number"
                  value={formData.total_amount}
                  disabled
                  size="small"
                  sx={disabledFieldSx}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Paid Amount"
                  type="number"
                  value={formData.paid_amount}
                  disabled
                  size="small"
                  sx={disabledFieldSx}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Remaining"
                  type="number"
                  value={formData.bal_amount}
                  disabled
                  size="small"
                  sx={disabledFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: T.primary,
                    mt: 0.5,
                  }}
                >
                  Payment Entries
                </Typography>
              </Grid>

              {rows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      name="pay_amount"
                      label="Pay Amount"
                      type="number"
                      size="small"
                      value={row.pay_amount}
                      onChange={(e) => handleRowChange(idx, e)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      name="payment_to"
                      label="Payment To"
                      size="small"
                      value={row.payment_to}
                      onChange={(e) => handleRowChange(idx, e)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      name="pay_mode"
                      label="Pay Mode"
                      size="small"
                      value={row.pay_mode}
                      onChange={(e) => handleRowChange(idx, e)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      name="upi_check_no"
                      label="UPI / Cheque No."
                      size="small"
                      value={row.upi_check_no}
                      onChange={(e) => handleRowChange(idx, e)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Tooltip title="Remove Row" arrow>
                      <IconButton
                        onClick={() => handleDeleteRow(idx)}
                        size="small"
                        sx={{
                          ...sq,
                          color: T.red,
                          bgcolor: T.redBg,
                          border: `1px solid ${T.redBorder}`,
                          width: 32,
                          height: 32,
                          mt: 0.25,
                          "&:hover": { bgcolor: "#fee2e2" },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </React.Fragment>
              ))}

              <Grid item xs={12}>
                <Button
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={handleAddRow}
                  size="small"
                  sx={{ ...ghostBtn, height: 32, px: 2 }}
                >
                  Add Another Payment
                </Button>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Total Remaining"
                  type="number"
                  value={(
                    Number(formData.bal_amount || 0) -
                    rows.reduce((acc, r) => acc + Number(r.pay_amount || 0), 0)
                  ).toFixed(2)}
                  disabled
                  size="small"
                  sx={disabledFieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Remark"
                  name="remark"
                  size="small"
                  value={formData.remark}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, remark: e.target.value }))
                  }
                  sx={inputSx}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 1,
              bgcolor: T.white,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            <Button
              size="small"
              onClick={() => setOpenAddDialog(false)}
              sx={{ ...ghostBtn, height: 32, px: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="small"
              sx={{ ...primaryBtn, height: 32, px: 2.5 }}
            >
              Submit Payment
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── View Dialog ── */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            background: "#6862e0",
            px: 2.5,
            py: 1.75,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.white,
              fontSize: "0.88rem",
              letterSpacing: "0.01em",
            }}
          >
            Payment Details
          </Typography>
          <IconButton
            onClick={() => setOpenViewDialog(false)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              p: 0.5,
              ...sq,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.15)",
                color: T.white,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
          {selectedRow && (
            <>
              <Box
                sx={{
                  p: 1.5,
                  ...sq,
                  bgcolor: "#fff",
                  border: `1.5px solid ${T.border}`,
                  mb: 2,
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: T.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      Payment ID
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                      {selectedRow.freight_payment_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: T.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      Branch
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                      {selectedRow.branch_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: T.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      Total Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: T.primary,
                      }}
                    >
                      {formatCurrency(selectedRow.total_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: T.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      Paid Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: T.success,
                      }}
                    >
                      {formatCurrency(selectedRow.paid_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: T.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      Remaining
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: selectedRow.rem_amount < 0 ? T.red : "#d97706",
                      }}
                    >
                      {formatCurrency(selectedRow.rem_amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        color: T.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      Payment Date
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                      {formatDate(selectedRow.created_at)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {selectedRow?.payments?.length > 0 && (
                <>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      color: T.primary,
                      mb: 1,
                    }}
                  >
                    💳 Payment Transactions
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      ...sq,
                      border: `1.5px solid ${T.border}`,
                      overflow: "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {[
                            "Amount",
                            "Mode",
                            "Payment To",
                            "Ref No.",
                            "Remark",
                          ].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                bgcolor: `${T.headerBg} !important`,
                                color: T.headerText,
                                borderBottom: `1px solid #ccc`,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRow.payments.map((p, i) => (
                          <TableRow
                            key={i}
                            sx={{
                              "& td": {
                                bgcolor: i % 2 === 0 ? T.baseRow : T.altRow,
                                fontSize: "0.72rem",
                                borderBottom: `1px solid #ccc`,
                              },
                            }}
                          >
                            <TableCell
                              sx={{ color: T.success, fontWeight: 600 }}
                            >
                              {formatCurrency(p.pay_amount)}
                            </TableCell>
                            <TableCell>{p.pay_mode || "—"}</TableCell>
                            <TableCell>{p.payment_to || "—"}</TableCell>
                            <TableCell>{p.upi_check_no || "—"}</TableCell>
                            <TableCell>{p.remark || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: T.white,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setOpenViewDialog(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ── */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <EditFreightPayment
          data={selectedRow}
          freightOptions={[]}
          branchOptions={[]}
          onClose={() => {
            setOpenEditDialog(false);
            fetchFreightPayment(1, debouncedLrNumber, debouncedPayDate);
          }}
          selectedFreight={selectedRow?.freights}
        />
      </Dialog>

      {/* ── Settle Popover ── */}
      <Popover
        open={Boolean(settleAnchorEl)}
        anchorEl={settleAnchorEl}
        onClose={() => {
          setSettleAnchorEl(null);
          setSettleId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            ...sq,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            border: `1.5px solid ${T.border}`,
            bgcolor: T.white,
          },
        }}
      >
        <Box sx={{ p: 2, maxWidth: 240 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                ...sq,
                bgcolor: T.successBg,
                border: `1.5px solid ${T.successBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircle sx={{ fontSize: 20, color: T.success }} />
            </Box>
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.78rem",
              color: T.text,
              textAlign: "center",
              mb: 0.5,
            }}
          >
            Settle this Payment?
          </Typography>
          <Typography
            sx={{
              fontSize: "0.67rem",
              color: T.textSub,
              fontWeight: 500,
              textAlign: "center",
              mb: 1.75,
            }}
          >
            This will mark the payment as settled.
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setSettleAnchorEl(null);
                setSettleId(null);
              }}
              sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              size="small"
              onClick={() => handleSettle(settleId)}
              sx={{ ...successBtn, height: 30, fontSize: "0.7rem" }}
            >
              Settle
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "80px !important" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            ...sq,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 600,
            fontSize: "0.82rem",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
